import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDollarSign,
  faArrowUp,
  faArrowDown,
  faServer,
  faDatabase,
  faNetworkWired,
  faChartLine,
  faChartBar
} from '@fortawesome/free-solid-svg-icons';
import { faGithub, faAws, faGoogle } from '@fortawesome/free-brands-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { CostSummary as CostSummaryType, CostData } from '../types';
import { CustomCostEntry, CSVImportResult } from './CustomDataTab';
import { ServiceBreakdownModal } from './ServiceBreakdownModal';
import { useAIAnalysis, AIInsightsPanel, AIInsightsButton } from './AIInsightsPanel';

interface CostSummaryProps {
  summary: CostSummaryType | null;
  loading?: boolean;
  error?: string | null;
  customCosts?: number;
  customEntries?: CustomCostEntry[];
  csvImportResults?: CSVImportResult[];
  costData?: CostData | null;
}

interface SummaryCardProps {
  title: string;
  value: string | number;
  icon: IconDefinition;
  color: 'blue' | 'green' | 'red' | 'yellow' | 'purple' | 'indigo' | 'gray';
  trend?: {
    direction: 'up' | 'down' | 'stable';
    percentage: number;
  };
  subtitle?: string;
  loading?: boolean;
  onClick?: () => void;
}

const colorClasses = {
  blue: {
    bg: 'bg-blue-100',
    text: 'text-blue-600',
    border: 'border-blue-200'
  },
  green: {
    bg: 'bg-green-100',
    text: 'text-green-600',
    border: 'border-green-200'
  },
  red: {
    bg: 'bg-red-100',
    text: 'text-red-600',
    border: 'border-red-200'
  },
  yellow: {
    bg: 'bg-yellow-100',
    text: 'text-yellow-600',
    border: 'border-yellow-200'
  },
  purple: {
    bg: 'bg-purple-100',
    text: 'text-purple-600',
    border: 'border-purple-200'
  },
  indigo: {
    bg: 'bg-indigo-100',
    text: 'text-indigo-600',
    border: 'border-indigo-200'
  },
  gray: {
    bg: 'bg-gray-100',
    text: 'text-gray-600',
    border: 'border-gray-200'
  }
};

const SummaryCard: React.FC<SummaryCardProps> = ({
  title,
  value,
  icon,
  color,
  trend,
  subtitle,
  loading = false,
  onClick
}) => {
  const colorClass = colorClasses[color];

  if (loading) {
    return (
      <div className="bg-white border border-gray-300 rounded p-3 animate-pulse">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="h-3 bg-gray-200 rounded mb-2"></div>
            <div className="h-5 bg-gray-200 rounded mb-1"></div>
            <div className="h-3 bg-gray-200 rounded w-2/3"></div>
          </div>
          <div className={`w-10 h-10 ${colorClass.bg} rounded`}></div>
        </div>
      </div>
    );
  }

  const getTrendIcon = () => {
    if (trend?.direction === 'up') return faArrowUp;
    if (trend?.direction === 'down') return faArrowDown;
    return faChartLine;
  };

  const getTrendColor = () => {
    if (trend?.direction === 'up') return 'text-red-600';
    if (trend?.direction === 'down') return 'text-green-600';
    return 'text-gray-600';
  };

  const formatValue = (val: string | number) => {
    if (typeof val === 'number') {
      if (val === 0) {
        return '$0';
      } else if (val >= 1000000) {
        return `$${(val / 1000000).toFixed(1)}M`;
      } else if (val >= 1000) {
        return `$${(val / 1000).toFixed(1)}K`;
      } else {
        return `$${val.toLocaleString()}`;
      }
    }
    return val;
  };

  return (
    <div className={`bg-white border ${colorClass.border} rounded p-3 transition-all hover:shadow-md ${onClick ? 'cursor-pointer' : ''}`} onClick={onClick}>
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="text-xs font-medium text-gray-600 mb-1">{title}</div>
          <div className="text-lg font-bold text-gray-900 mb-1">
            {formatValue(value)}
          </div>
          {(subtitle || trend) && (
            <div className="text-xs text-gray-500 flex items-center">
              {subtitle && <span>{subtitle}</span>}
              {trend && (
                <span className={`ml-2 flex items-center ${getTrendColor()}`}>
                  <FontAwesomeIcon icon={getTrendIcon()} className="mr-1 text-xs" />
                  {trend.percentage.toFixed(1)}%
                </span>
              )}
            </div>
          )}
        </div>
        <div className={`w-10 h-10 ${colorClass.bg} rounded flex items-center justify-center flex-shrink-0`}>
          <FontAwesomeIcon icon={icon} className={`${colorClass.text} text-sm`} />
        </div>
      </div>
    </div>
  );
};



export const CostSummary: React.FC<CostSummaryProps> = ({ summary, loading = false, error = null, customCosts = 0, customEntries = [], csvImportResults = [], costData = null }) => {
  const { aiAnalysis, showAIInsights, analyzing, fetchAnalysis, setShowAIInsights } = useAIAnalysis('summary');
  const [showServiceBreakdown, setShowServiceBreakdown] = useState(false);

  const handleAIAnalysis = async () => {
    if (!summary) return;
    await fetchAnalysis({
      summary,
      byType: summary.byType,
      byProvider: summary.byProvider,
      customCosts,
      customEntries,
      totalSpend: summary.totalSpend,
      monthlyDelta: summary.monthlyDelta
    });
  };

  // Handle loading state
  if (loading) {
    return (
      <div className="space-y-3">
        <div className="bg-white border border-gray-300 rounded p-3">
          <h3 className="text-sm font-bold text-gray-900 mb-1">Cost Summary</h3>
          <p className="text-xs text-gray-600">Loading cost data...</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white rounded border border-gray-300 p-3 animate-pulse">
              <div className="h-3 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div className="h-5 bg-gray-200 rounded w-1/2 mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-1/4"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Handle error state or no data
  if (error || !summary) {
    return (
      <div className="space-y-3">
        <div className="bg-white border border-gray-300 rounded p-3">
          <h3 className="text-sm font-bold text-gray-900 mb-1">Cost Summary</h3>
          <p className="text-xs text-gray-600">
            Overview of your multi-cloud spending across AWS, GCP, and GitHub
          </p>
        </div>
        <div className="bg-white rounded border border-gray-300 p-4 text-center">
          <FontAwesomeIcon icon={faDollarSign} className="text-3xl text-gray-400 mb-3" />
          <h4 className="text-sm font-bold text-gray-900 mb-1">
            {error ? 'Unable to Load Cost Data' : 'No Cost Data Available'}
          </h4>
          <p className="text-xs text-gray-600 mb-3">
            {error
              ? 'There was an issue connecting to your cloud accounts. Please check your account connections.'
              : 'Connect your cloud accounts (AWS, GCP, GitHub) to view cost analysis data.'
            }
          </p>
          <button className="bg-blue-600 text-white px-3 py-1.5 rounded text-xs hover:bg-blue-700 transition-colors">
            {error ? 'Retry' : 'Connect Accounts'}
          </button>
        </div>
      </div>
    );
  }

  // Calculate previous month spend (current - delta)
  const previousMonthSpend = summary.totalSpend - summary.monthlyDelta;

  // Calculate total line items from actual cloud data (all accounts/providers)
  let totalLineItems = 0;
  if (costData) {
    costData.clouds.aws.accounts.forEach(a => { totalLineItems += a.services.length; });
    costData.clouds.gcp.projects.forEach(p => { totalLineItems += p.services.length; });
    costData.clouds.github.orgs.forEach(o => { totalLineItems += o.usage.length; });
  }
  if (totalLineItems === 0) {
    totalLineItems = (summary as any).serviceCount || (
      (summary.byProvider.aws > 0 ? 1 : 0) +
      (summary.byProvider.gcp > 0 ? 1 : 0) +
      (summary.byProvider.github > 0 ? 1 : 0)
    );
  }
  totalLineItems += customEntries.length;

  return (
    <div className="space-y-3">
      {/* AI Insights Button */}
      <div className="flex justify-start">
        <AIInsightsButton
          onClick={handleAIAnalysis}
          loading={analyzing}
          disabled={loading}
        />
      </div>

      {/* AI Analysis Panel */}
      {showAIInsights && aiAnalysis && (
        <AIInsightsPanel
          analysis={aiAnalysis}
          onClose={() => setShowAIInsights(false)}
        />
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {/* Card 1: Total Monthly Spend (Current Month) */}
        <SummaryCard
          title="Total Monthly Spend"
          value={summary.totalSpend + customCosts}
          icon={faDollarSign}
          color="blue"
          trend={summary.totalSpend > 0 ? {
            direction: summary.monthlyDelta >= 0 ? 'up' : 'down',
            percentage: parseFloat(Math.abs((summary.monthlyDelta / summary.totalSpend) * 100).toFixed(1))
          } : undefined}
          loading={loading}
        />

        {/* Card 2: Custom Costs */}
        <SummaryCard
          title="Custom Costs"
          value={customCosts}
          icon={faDatabase}
          color="indigo"
          subtitle={`${customEntries.length} entries`}
          loading={loading}
        />

        {/* Card 3: Last Month Spend */}
        <SummaryCard
          title="Last Month Spend"
          value={previousMonthSpend > 0 ? previousMonthSpend : 0}
          icon={faChartLine}
          color="green"
          loading={loading}
        />

        {/* Card 4: SKUs / Line Items */}
        <SummaryCard
          title="Cost Line Items"
          value={String(totalLineItems)}
          icon={faChartBar}
          color="purple"
          subtitle="active SKUs tracked"
          loading={loading}
          onClick={() => setShowServiceBreakdown(true)}
        />
      </div>

      {/* CSV Import Analysis Blocks */}
      {csvImportResults && csvImportResults.length > 0 && (
        <div className="space-y-3">
          <div className="bg-white border border-gray-300 rounded p-3">
            <h4 className="text-sm font-bold text-gray-900 mb-1 flex items-center">
              <FontAwesomeIcon icon={faChartLine} className="text-purple-600 mr-2 text-sm" />
              Imported Data Analysis
            </h4>
            <p className="text-xs text-gray-600">
              Intelligent insights from your imported CSV data with automated categorization and recommendations
            </p>
          </div>

          {csvImportResults.map((result, index) => (
            <div key={index} className="bg-white border border-gray-300 rounded p-3">
              <div className="flex items-center justify-between mb-2">
                <h5 className="text-sm font-bold text-gray-900">Import #{index + 1} Analysis</h5>
                <span className="text-xs text-gray-500">
                  {result.entries.length} entries • ${result.summary.totalCost.toLocaleString()} total
                </span>
              </div>

              {/* Key Insights */}
              <div className="space-y-2">
                <div>
                  <h6 className="text-xs font-bold text-gray-700 mb-1">Key Insights</h6>
                  <div className="space-y-1">
                    {result.summary.insights.slice(0, 3).map((insight, insightIndex) => (
                      <div key={insightIndex} className="text-xs text-gray-600 flex items-start">
                        <span className="text-blue-500 mr-2">•</span>
                        <span>{insight}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Category Breakdown */}
                {Object.keys(result.summary.categories).length > 0 && (
                  <div>
                    <h6 className="text-xs font-bold text-gray-700 mb-1">Category Distribution</h6>
                    <div className="grid grid-cols-2 gap-2">
                      {Object.entries(result.summary.categories).slice(0, 4).map(([category, count]) => (
                        <div key={category} className="flex items-center justify-between text-xs">
                          <span className="text-gray-600">{category}</span>
                          <span className="font-medium text-gray-900">{count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Visualizations Available */}
                {result.summary.visualizations && result.summary.visualizations.length > 0 && (
                  <div>
                    <h6 className="text-xs font-bold text-gray-700 mb-1">Available Visualizations</h6>
                    <div className="flex flex-wrap gap-2">
                      {result.summary.visualizations.map((viz, vizIndex) => (
                        <span key={vizIndex} className="inline-flex items-center px-2 py-1 rounded text-xs bg-indigo-100 text-indigo-700">
                          <FontAwesomeIcon icon={faChartBar} className="mr-1 text-xs" />
                          {viz.title}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Cost Breakdown by Provider and Custom Costs */}
      <div className="bg-white border border-gray-300 rounded p-3">
        <h4 className="text-sm font-bold text-gray-900 mb-3">Cost Breakdown by Provider</h4>
        {loading ? (
          <div className="space-y-2 animate-pulse">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="w-6 h-6 bg-gray-200 rounded"></div>
                  <div className="h-3 bg-gray-200 rounded w-16"></div>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="h-3 bg-gray-200 rounded w-12"></div>
                  <div className="h-3 bg-gray-200 rounded w-8"></div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {[
              // Provider totals from connected accounts
              ...(summary.byProvider.aws > 0 ? [{
                name: 'AWS',
                value: summary.byProvider.aws,
                icon: faAws,
                color: 'text-orange-600',
                isProvider: true
              }] : []),
              ...(summary.byProvider.gcp > 0 ? [{
                name: 'GCP',
                value: summary.byProvider.gcp,
                icon: faGoogle,
                color: 'text-blue-600',
                isProvider: true
              }] : []),
              ...(summary.byProvider.github > 0 ? [{
                name: 'GitHub',
                value: summary.byProvider.github,
                icon: faGithub,
                color: 'text-gray-600',
                isProvider: true
              }] : []),
              // Custom cost line items
              ...customEntries.map(entry => ({
                name: entry.vendorName,
                value: entry.amount,
                icon: entry.costType === 'SaaS' ? faDatabase :
                      entry.costType === 'License' ? faServer :
                      entry.costType === 'Security Tool' ? faNetworkWired : faDollarSign,
                color: entry.costType === 'SaaS' ? 'text-purple-600' :
                       entry.costType === 'License' ? 'text-green-600' :
                       entry.costType === 'Security Tool' ? 'text-red-600' : 'text-indigo-600',
                isProvider: false
              }))
            ]
              .sort((a, b) => b.value - a.value)
              .map((type) => {
                const totalSpendWithCustom = summary.totalSpend + customCosts;
                const percentage = totalSpendWithCustom > 0 ? (type.value / totalSpendWithCustom) * 100 : 0;
                return (
                  <div key={type.name} className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <FontAwesomeIcon icon={type.icon} className={`${type.color} text-sm`} />
                      <span className="text-sm font-medium text-gray-700">
                        {type.name}
                        {type.isProvider && type.value === 0 && <span className="ml-1 text-xs text-gray-500">(not connected)</span>}
                      </span>
              
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className="text-sm font-bold text-gray-900">
                        ${type.value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                      <span className="text-xs text-gray-500 w-12 text-right">
                        {percentage.toFixed(1)}%
                      </span>
                      <div className="w-20 bg-gray-200 rounded-full h-2">
                        <div
                          className="h-2 rounded-full bg-gradient-to-r from-blue-400 to-green-400"
                          style={{ width: `${Math.min(percentage, 100)}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                );
              })}
            {/* Show total - sum of displayed provider values + custom costs */}
            <div className="pt-2 mt-2 border-t border-gray-300">
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold text-gray-900">Total</span>
                <span className="text-sm font-bold text-gray-900">
                  ${(summary.byProvider.aws + summary.byProvider.gcp + summary.byProvider.github + customCosts).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Service Breakdown Modal */}
      <ServiceBreakdownModal
        isOpen={showServiceBreakdown}
        onClose={() => setShowServiceBreakdown(false)}
        data={costData}
      />
    </div>
  );
};